import type { ReactNode, RefObject } from 'react';
import { Icon } from '@iconify/react';
import { Chip, Spinner } from '@nextui-org/react';
import { useTranslation } from 'react-i18next';
import { isPdfFile } from './claimIntakeDocumentView';

export function ClaimIntakeUploadDropZoneContent({
  isUploading,
  loadingMessage,
  idleTitle,
  idleHint,
  idleIcon = 'mdi:cloud-upload-outline',
}: Readonly<{
  isUploading: boolean;
  loadingMessage: string;
  idleTitle: string;
  idleHint: string;
  idleIcon?: string;
}>) {
  if (isUploading) {
    return (
      <div className="flex flex-col items-center gap-2">
        <Spinner size="md" />
        <p className="text-sm text-primary font-medium">{loadingMessage}</p>
      </div>
    );
  }
  return (
    <>
      <Icon icon={idleIcon} width={32} className="text-default-400 mx-auto mb-2" />
      <p className="text-sm text-default-500">{idleTitle}</p>
      <p className="text-xs text-default-400 mt-1">{idleHint}</p>
    </>
  );
}

export function ClaimIntakeUploadedFileRow({
  file,
  onRemove,
  onViewPdf,
}: Readonly<{
  file: File;
  onRemove: () => void;
  onViewPdf?: (file: File) => void;
}>) {
  const { t } = useTranslation();
  const showViewPdf = Boolean(onViewPdf) && isPdfFile(file);
  return (
    <div className="flex items-center gap-1.5 text-xs text-default-500 min-w-0 py-0.5">
      <Icon icon="mdi:file-outline" width={14} className="text-default-400 shrink-0" />
      <span className="truncate flex-1 min-w-0" title={file.name}>
        {file.name}
      </span>
      {showViewPdf ? (
        <button
          type="button"
          className="shrink-0 p-0.5 rounded text-primary hover:bg-primary/10 transition-colors"
          onClick={() => onViewPdf?.(file)}
          aria-label={t('View PDF')}
          title={t('View PDF')}
        >
          <Icon icon="mdi:eye-outline" width={15} />
        </button>
      ) : null}
      <button
        type="button"
        className="shrink-0 p-0.5 text-default-400 hover:text-red-500 transition-colors"
        onClick={onRemove}
        aria-label={t('Remove uploaded file')}
      >
        <Icon icon="mdi:close" width={14} />
      </button>
    </div>
  );
}

export function ClaimIntakeFileUploadCard({
  title,
  badgeLabel = 'Optional',
  description,
  isUploading,
  isDisabled = false,
  loadingMessage,
  idleTitle,
  idleHint,
  idleIcon,
  accept,
  uploadError,
  detailError,
  uploadedFiles,
  headerExtra,
  chips,
  footerExtra,
  onFileDrop,
  onFileSelect,
  onPickFile,
  onRemoveFile,
  onViewPdf,
  fileInputRef,
}: Readonly<{
  title: string;
  badgeLabel?: string;
  description?: string;
  isUploading: boolean;
  /** Disables the drop zone without showing the upload spinner (e.g. while contract lookup runs). */
  isDisabled?: boolean;
  loadingMessage: string;
  idleTitle: string;
  idleHint: string;
  idleIcon?: string;
  /** When omitted, all file types are accepted. */
  accept?: string;
  uploadError?: string | null;
  detailError?: ReactNode;
  uploadedFiles: readonly File[];
  headerExtra?: ReactNode;
  /** Prompt chips (e.g. TWE required photos) shown above the drop zone. */
  chips?: readonly string[];
  footerExtra?: ReactNode;
  onFileDrop: (e: React.DragEvent<HTMLButtonElement>) => void;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPickFile: () => void;
  onRemoveFile: (index: number) => void;
  onViewPdf?: (file: File) => void;
  fileInputRef: RefObject<HTMLInputElement>;
}>) {
  const interactionLocked = isUploading || isDisabled;
  let dropZoneClass = 'border-default-300 cursor-pointer hover:border-primary/40';
  if (isUploading) {
    dropZoneClass = 'border-primary/40 bg-primary/5 cursor-wait';
  } else if (isDisabled) {
    dropZoneClass = 'border-default-200 bg-default-100 cursor-not-allowed opacity-60';
  }

  return (
    <div className="bg-content1 rounded-xl border border-default-200 p-4">
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1">
        <h2 className="text-base sm:text-lg font-semibold text-foreground">{title}</h2>
        <Chip size="sm" color="primary" variant="flat" className="uppercase text-[10px] font-bold px-2">
          {badgeLabel}
        </Chip>
        {headerExtra}
      </div>
      {description ? <p className="text-sm text-default-500 mb-3">{description}</p> : null}
      {chips && chips.length > 0 ? (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {chips.map((chip) => (
            <Chip key={chip} size="sm" variant="flat" color="primary" className="h-auto py-1 whitespace-normal">
              {chip}
            </Chip>
          ))}
        </div>
      ) : null}
      <button
        type="button"
        className={`w-full border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dropZoneClass}`}
        onDragOver={(e) => e.preventDefault()}
        onDrop={interactionLocked ? undefined : onFileDrop}
        onClick={interactionLocked ? undefined : onPickFile}
        disabled={interactionLocked}
      >
        <ClaimIntakeUploadDropZoneContent
          isUploading={isUploading}
          loadingMessage={loadingMessage}
          idleTitle={idleTitle}
          idleHint={idleHint}
          idleIcon={idleIcon}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple
          className="hidden"
          onChange={onFileSelect}
          disabled={interactionLocked}
        />
      </button>

      {uploadError ? (
        <div className="mt-3 p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/40 rounded-lg text-sm text-red-600 dark:text-red-400">{uploadError}</div>
      ) : null}

      {detailError ? <div className="mt-3">{detailError}</div> : null}

      {uploadedFiles.length > 0 ? (
        <div className="mt-2 space-y-0.5">
          {uploadedFiles.map((file, index) => (
            <ClaimIntakeUploadedFileRow
              key={`${file.name}-${file.lastModified}-${index}`}
              file={file}
              onRemove={() => onRemoveFile(index)}
              onViewPdf={onViewPdf}
            />
          ))}
        </div>
      ) : null}

      {footerExtra}
    </div>
  );
}
